import type { ColumnState } from '@crm/contracts';
import { ArrowDown, ArrowUp, Columns3, Pin, PinOff } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx';

/**
 * The Columns dialog (RELAY-942): show/hide, reorder (up/down) and pin left. Operates on the
 * `ColumnState` the table and the page share, so what the user does here lands in the URL and
 * in saved views unchanged. Up/down buttons rather than drag-and-drop: keyboard-reachable and
 * no extra dependency; a drag handle can come later without changing the state shape.
 */
export function ColumnsMenu({
  columnIds,
  label,
  state,
  onChange,
}: {
  columnIds: readonly string[];
  label: (id: string) => string;
  state: ColumnState;
  onChange: (next: ColumnState) => void;
}) {
  const [open, setOpen] = useState(false);
  const order = [
    ...state.order.filter((id) => columnIds.includes(id)),
    ...columnIds.filter((id) => !state.order.includes(id)),
  ];
  const move = (id: string, delta: -1 | 1) => {
    const i = order.indexOf(id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange({ ...state, order: next });
  };
  const toggleHidden = (id: string) =>
    onChange({
      ...state,
      hidden: state.hidden.includes(id)
        ? state.hidden.filter((h) => h !== id)
        : [...state.hidden, id],
    });
  const togglePin = (id: string) =>
    onChange({
      ...state,
      pinned: state.pinned.includes(id)
        ? state.pinned.filter((p) => p !== id)
        : [...state.pinned, id],
    });
  const hiddenCount = state.hidden.filter((id) => columnIds.includes(id)).length;

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="columns-menu">
        <Columns3 /> Columns{hiddenCount ? ` (${hiddenCount} hidden)` : ''}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Columns</DialogTitle>
            <DialogDescription>
              Show, hide, reorder and pin. Changes are part of the view.
            </DialogDescription>
          </DialogHeader>
          <ul className="flex flex-col gap-1" data-testid="columns-list">
            {order.map((id, i) => {
              const hidden = state.hidden.includes(id);
              const pinned = state.pinned.includes(id);
              return (
                <li
                  key={id}
                  className="flex items-center gap-2 text-sm"
                  data-testid={`column-${id}`}
                >
                  <label className="flex flex-1 items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!hidden}
                      onChange={() => toggleHidden(id)}
                      aria-label={`Show ${label(id)}`}
                    />
                    <span className={hidden ? 'text-muted-foreground' : ''}>{label(id)}</span>
                  </label>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`${pinned ? 'Unpin' : 'Pin'} ${label(id)}`}
                    onClick={() => togglePin(id)}
                  >
                    {pinned ? <PinOff /> : <Pin />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Move ${label(id)} up`}
                    disabled={i === 0}
                    onClick={() => move(id, -1)}
                  >
                    <ArrowUp />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Move ${label(id)} down`}
                    disabled={i === order.length - 1}
                    onClick={() => move(id, 1)}
                  >
                    <ArrowDown />
                  </Button>
                </li>
              );
            })}
          </ul>
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange({ order: [], hidden: [], sizes: {}, pinned: [] })}
            >
              Reset
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
